import { useMemo } from 'react';

import { FulfillmentStrategy } from '@invenco/products-interface';

import { Card, CardHeader } from '@/components/core/card';
import { DataTable, useDataTable } from '@/components/data-table';
import { DataTableCardBody, DataTableCardTitle } from '@/components/data-table/data-table-card';

import { OrderLineExpandedDetails } from './order-line-expanded-details';
import { getOrderLineColumns, type OrderLine } from './order-lines-card-columns';
import { OrderTotals, type OrderLinesOrderContext } from './order-totals';
import { ShowMoreOrderLinesButton } from './show-more-order-lines-button';
import { useCardOrderLines } from './use-card-order-lines';

type OrderLinesCardProps = {
  title: string;
  lines: OrderLine[];
  order: OrderLinesOrderContext;
  showTotals?: boolean;
  isLoading?: boolean;
  isLoadingWithStaleData?: boolean;
  hasError?: boolean;
  initialVisibleLineCount?: number;
};

export function OrderLinesCard({
  title,
  lines,
  order,
  showTotals = false,
  isLoading,
  isLoadingWithStaleData,
  hasError,
  initialVisibleLineCount,
}: OrderLinesCardProps) {
  const { visibleLines, renderedLines, hiddenLineCount, isLimitingLines, showAllLines } =
    useCardOrderLines({
      lines,
      initialVisibleLineCount,
    });
  const reserveTypeIconSpace = renderedLines.some(
    (line) => line.fulfillmentStrategy === FulfillmentStrategy.BUNDLE,
  );
  const columns = useMemo(
    () => getOrderLineColumns({ orderCurrency: order.currency, reserveTypeIconSpace }),
    [order.currency, reserveTypeIconSpace],
  );

  const table = useDataTable({
    data: renderedLines,
    columns,
    isLoading,
    isLoadingWithStaleData,
    hasError,
    enableRowExpansion: true,
    getSubRows: (row) => row.components,
    getRowCanExpand: (row) => !row.original.assemblyOrderLineId,
    manualPagination: false,
    manualSorting: false,
  });

  return (
    <Card aria-label={title}>
      <CardHeader>
        <DataTableCardTitle table={table} rowCount={visibleLines.length}>
          {title}
        </DataTableCardTitle>
      </CardHeader>
      <DataTableCardBody table={table} emptyMessage="No items found">
        <DataTable
          table={table}
          getRowCanClick={(row) => row.getCanExpand()}
          onClickRow={(row) => {
            if (row.getCanExpand()) row.toggleExpanded();
          }}
          renderExpandedRow={(row) => (
            <OrderLineExpandedDetails
              line={row.original}
              channelId={order.channelId ?? undefined}
            />
          )}
        />
      </DataTableCardBody>
      {isLimitingLines ? (
        <ShowMoreOrderLinesButton hiddenLineCount={hiddenLineCount} onClick={showAllLines} />
      ) : null}
      {showTotals ? <OrderTotals order={order} lines={lines} isLoading={isLoading} /> : null}
    </Card>
  );
}
